interface AtomOrbit {
	phase: number
	radiusX: number
	radiusY: number
	rotation: number
	speed: number
	thickness: number
	electronRadius: number
	electronOffset: number
}

export class AtomSpinner {
	private canvas: HTMLCanvasElement | null = null
	private context: CanvasRenderingContext2D | null = null
	private frameId: number | null = null
	private startedAt = 0
	private dpr = 1
	private baseColor = 'rgb(128, 128, 128)'

	private readonly orbits: AtomOrbit[] = [
		{
			phase: 0.15,
			radiusX: 92,
			radiusY: 36,
			rotation: -0.45,
			speed: 1.15,
			thickness: 2,
			electronRadius: 5,
			electronOffset: 0,
		},
		{
			phase: 1.28,
			radiusX: 72,
			radiusY: 58,
			rotation: 0.82,
			speed: -0.86,
			thickness: 1.5,
			electronRadius: 4,
			electronOffset: 1.8,
		},
		{
			phase: 2.52,
			radiusX: 104,
			radiusY: 24,
			rotation: 0.35,
			speed: 0.74,
			thickness: 1.75,
			electronRadius: 4.5,
			electronOffset: 0.9,
		},
		{
			phase: 3.1,
			radiusX: 56,
			radiusY: 96,
			rotation: -0.95,
			speed: 1.38,
			thickness: 1.5,
			electronRadius: 3.8,
			electronOffset: 2.7,
		},
		{
			phase: 4.22,
			radiusX: 118,
			radiusY: 46,
			rotation: 0.22,
			speed: -1.05,
			thickness: 1.25,
			electronRadius: 3.5,
			electronOffset: 3.4,
		},
		{
			phase: 5.08,
			radiusX: 84,
			radiusY: 84,
			rotation: -0.14,
			speed: 0.58,
			thickness: 1.25,
			electronRadius: 3.75,
			electronOffset: 1.2,
		},
	]

	public setColor(color: string): void {
		// eslint-disable-next-line no-console
		console.log(`AtomSpinner: Received color ${color}`)
		const normalized = color.trim()
		// eslint-disable-next-line no-console
		console.log(`AtomSpinner: Setting color to ${normalized}`)
		if (normalized) {
			this.baseColor = normalized
		}
	}

	public mount(canvas: HTMLCanvasElement | null | undefined): boolean {
		if (!(canvas instanceof HTMLCanvasElement)) {
			return false
		}

		this.canvas = canvas
		this.context = canvas.getContext('2d')
		return this.context !== null
	}

	public start(canvas?: HTMLCanvasElement | null): boolean {
		if (canvas) {
			this.mount(canvas)
		}

		if (!this.canvas || !this.context) {
			return false
		}

		this.startedAt = performance.now()
		this.stop()
		this.frameId = window.requestAnimationFrame(this.renderFrame)
		return true
	}

	public stop(): void {
		if (this.frameId !== null) {
			window.cancelAnimationFrame(this.frameId)
			this.frameId = null
		}

		if (!this.canvas || !this.context) {
			return
		}

		this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
	}

	private renderFrame = (timestamp: number): void => {
		if (!this.canvas || !this.context) {
			return
		}

		const metrics = this.syncCanvas()
		if (!metrics) {
			this.frameId = window.requestAnimationFrame(this.renderFrame)
			return
		}

		const { width, height } = metrics
		const ctx = this.context
		const elapsed = (timestamp - this.startedAt) / 1000
		const centerX = width / 2
		const centerY = height / 2
		const atomRadius = Math.min(width, height) * 0.13

		ctx.clearRect(0, 0, width, height)

		ctx.save()
		ctx.translate(centerX, centerY)

		for (const orbit of this.orbits) {
			this.drawOrbit(ctx, orbit, elapsed)
		}

		this.drawCore(ctx, atomRadius)
		ctx.restore()

		this.frameId = window.requestAnimationFrame(this.renderFrame)
	}

	private syncCanvas(): { width: number, height: number } | null {
		if (!this.canvas || !this.context) {
			return null
		}

		const rect = this.canvas.getBoundingClientRect()
		const width = Math.max(1, Math.round(rect.width || this.canvas.width || 240))
		const height = Math.max(1, Math.round(rect.height || this.canvas.height || 240))
		const dpr = Math.max(1, window.devicePixelRatio || 1)

		if (this.canvas.width !== Math.round(width * dpr) || this.canvas.height !== Math.round(height * dpr) || this.dpr !== dpr) {
			this.dpr = dpr
			this.canvas.width = Math.round(width * dpr)
			this.canvas.height = Math.round(height * dpr)
			this.context.setTransform(dpr, 0, 0, dpr, 0, 0)
		}

		return { width, height }
	}

	private drawOrbit(
		ctx: CanvasRenderingContext2D,
		orbit: AtomOrbit,
		elapsed: number,
	): void {
		const angle = orbit.phase + elapsed * orbit.speed
		const cosRotation = Math.cos(orbit.rotation)
		const sinRotation = Math.sin(orbit.rotation)

		ctx.save()
		ctx.lineWidth = orbit.thickness
		ctx.strokeStyle = this.baseColor
		ctx.beginPath()
		ctx.ellipse(0, 0, orbit.radiusX, orbit.radiusY, orbit.rotation, 0, Math.PI * 2)
		ctx.stroke()

		const localX = Math.cos(angle) * orbit.radiusX
		const localY = Math.sin(angle) * orbit.radiusY
		const x = localX * cosRotation - localY * sinRotation
		const y = localX * sinRotation + localY * cosRotation

		ctx.fillStyle = this.baseColor
		ctx.beginPath()
		ctx.arc(x, y, orbit.electronRadius, 0, Math.PI * 2)
		ctx.fill()

		ctx.fillStyle = this.baseColor
		ctx.beginPath()
		ctx.arc(
			x + Math.cos(angle + orbit.electronOffset) * 1.4,
			y + Math.sin(angle + orbit.electronOffset) * 1.4,
			Math.max(1, orbit.electronRadius * 0.38),
			0,
			Math.PI * 2,
		)
		ctx.fill()
		ctx.restore()
	}

	private drawCore(ctx: CanvasRenderingContext2D, atomRadius: number): void {
		ctx.save()
		ctx.fillStyle = this.baseColor
		ctx.beginPath()
		ctx.arc(0, 0, atomRadius * 1.15, 0, Math.PI * 2)
		ctx.fill()
		ctx.restore()
	}
}
